import { BreadcrumbList, SocialMediaPosting, WithContext } from 'schema-dts';
import { useVideoJsonLD } from '@article/head/json-ld/use-video-jsonld';
import { useCommentsJsonLD } from '@article/head/json-ld/use-comments-jsonld';
import { useArticleAuthorJsonld } from '@article/head/json-ld/use-author-jsonld';
import { useArticleBaseJsonLD } from '@article/head/json-ld/use-article-base-jsonld';
import { FC, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { $CurrentAvailableArticleOrNull } from '@article/shared/atoms/current-article';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { ArticleDetailUtil, EArticleClass } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { removeEscapedSpecialXmlCharacter } from '~/utils/escape-xml';
import { useSEOLinkBuilder } from '~/utils/build-seo-url';
import { DynamicHead } from '@lemon8/web-app-shared/ssr';
import { loadable } from 'jotai/utils';

export const ArticleJsonLD: FC = () => {
  const articleBaseJsonLD = useArticleBaseJsonLD();
  const authorJsonLD = useArticleAuthorJsonld();
  const videoJsonLD = useVideoJsonLD();
  const commentJsonLD = useCommentsJsonLD();
  const loadableArticle = useAtomValue(loadable($CurrentAvailableArticleOrNull));

  if (!articleBaseJsonLD || loadableArticle.state !== 'hasData') {
    return null;
  }

  const article = loadableArticle.data;

  const articleJsonLd: WithContext<SocialMediaPosting> = {
    '@context': 'https://schema.org',
    ...articleBaseJsonLD,
  };

  if (authorJsonLD) {
    articleJsonLd.author = authorJsonLD;
  }

  if (commentJsonLD && article && !article.isTranslatedVersion) {
    articleJsonLd.comment = commentJsonLD;
  }

  if (videoJsonLD) {
    articleJsonLd.video = videoJsonLD;
  }

  return (
    <DynamicHead>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
    </DynamicHead>
  );
};

export const BreadCrumbJsonLD: FC = () => {
  const loadableArticle = useAtomValue(loadable($CurrentAvailableArticleOrNull));
  const { region } = useAtomValue($Locale);
  const { t } = useTranslation();
  const buildSEOLink = useSEOLinkBuilder();

  const articleTitle = useMemo(() => {
    if (loadableArticle.state !== 'hasData' || !loadableArticle.data) {
      return '';
    }

    const article = loadableArticle.data;

    // 2. try getting by article title
    let { title } = article;
    title = removeEscapedSpecialXmlCharacter(title.replace(/\s+/g, ' '));
    if (title) {
      return title;
    }

    if (article.articleClass === EArticleClass.GALLERY) {
      return `${t('user_published_gallery').replace('%s', article.author.nickName)} | Lemon8`;
    } else if (article.articleClass === EArticleClass.VIDEO) {
      return `${t('own_video').replace('%s', article.author.nickName)} | Lemon8`;
    } else {
      return `${t('user_published_article').replace('%s', article.author.nickName)} | Lemon8`;
    }
  }, [loadableArticle]);

  if (
    loadableArticle.state !== 'hasData' ||
    !loadableArticle.data ||
    ArticleDetailUtil.isTranslatedArticleToShow(loadableArticle.data, region)
  ) {
    return null;
  }

  const article = loadableArticle.data;

  const breadCrumbList: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Thing',
          '@id': `https://www.lemon8-app.com/feed/foryou?region=${region}`,
          name: 'lemon8',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Thing',
          '@id': buildSEOLink(ArticleDetailUtil.getAuthorPath(article), { fullUrl: true }),
          name: article.author.nickName,
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Thing',
          '@id': buildSEOLink(ArticleDetailUtil.getArticlePath(article), { fullUrl: true }),
          name: articleTitle,
        },
      },
    ],
  };

  return (
    <DynamicHead>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadCrumbList) }} />
    </DynamicHead>
  );
};
